/**
 * Image Utilities
 *
 * Client-side image compression and resizing for optimal storage and loading.
 */

/**
 * Image compression presets
 */
export const IMAGE_PRESETS = {
  full: {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.8,
  },
};

/**
 * Compress and resize an image file
 *
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Max width in pixels (default: 1600)
 * @param {number} options.maxHeight - Max height in pixels (default: 1600)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.9)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 1600,
    maxHeight = 1600,
    quality = 0.9,
  } = options;

  // Skip if not an image
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Skip small files (under 100KB) - probably already optimized
  if (file.size < 100 * 1024) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            // Fallback to original if conversion fails
            resolve(file);
            return;
          }

          // Create new File object with same name
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          // Only use compressed version if it's actually smaller
          if (compressedFile.size < file.size) {
            console.log(
              `[imageUtils] Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB -> ${(compressedFile.size / 1024).toFixed(0)}KB`
            );
            resolve(compressedFile);
          } else {
            console.log(`[imageUtils] Keeping original ${file.name} (compression larger)`);
            resolve(file);
          }
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      // Return original file on error
      console.warn('[imageUtils] Failed to load image, using original');
      resolve(file);
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Create a thumbnail from an image file
 *
 * @param {File} file - The image file
 * @param {Object} options - Thumbnail options
 * @param {number} options.maxSize - Max dimension in pixels (default: 400)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.7)
 * @returns {Promise<File>} - Thumbnail image file
 */
export async function createThumbnail(file, options = {}) {
  const { maxSize = 400, quality = 0.7 } = options;

  return compressImage(file, {
    maxWidth: maxSize,
    maxHeight: maxSize,
    quality,
  });
}

/**
 * Get image dimensions
 *
 * @param {File} file - The image file
 * @returns {Promise<{width: number, height: number}>}
 */
export function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}
